// react
import { useMemo, useState } from 'react';
// redux
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

// material ui
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';

// api
import {
  useAlarmGroupsDeleteAsyncByidMutation,
  useAlarmGroupsGetAsyncByidQuery,
  useAlarmTypesGetCollectionAsyncByqueryOptionsQuery,
} from '@/api/generated/recalc/endpoints/v1/alarms';
import type {
  ReadAlarmGroupDto,
  ReadAlarmTypeDto,
} from '@/api/generated/recalc/endpoints/v1/alarms';
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';
import { useGetAlarmGroupsByAccountQuery } from '@/api/recalc/endpoints/alarmGroupsApiSlice';

// shared components
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import AlarmGroupDataGrid from '@/features/alarm-hub/components/alarm-group/AlarmGroupDataGrid';
import DeleteAlarmGroupDialog from '@/features/alarm-hub/components/alarm-group/DeleteAlarmGroupDialog';
import CreateAlarmGroupForm from '@/features/alarm-hub/components/alarm-group/forms/CreateAlarmGroupForm';
import EditAlarmGroupForm from '@/features/alarm-hub/components/alarm-group/forms/EditAlarmGroupForm';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { showSnackbar } from '@/redux/snackbarSlice';

type EnergyXplorerAdvancedAlarmGroupsDialogProps = {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
};

type DialogMode = 'list' | 'create' | 'edit';

type AlarmGroupSubscriptionShape = {
  User?: {
    UserId?: number;
  };
};

type AlarmGroupShape = {
  AlarmSubscriptions?: AlarmGroupSubscriptionShape[];
};

export default function EnergyXplorerAdvancedAlarmGroupsDialog({
  open,
  onClose,
  accountId,
}: EnergyXplorerAdvancedAlarmGroupsDialogProps) {
  const { t } = useTranslation(['alarms', 'energy-analysis']);
  const dispatch = useDispatch();
  const { session } = useSessionAuth();
  const [mode, setMode] = useState<DialogMode>('list');
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<ReadAlarmGroupDto | null>(
    null,
  );

  const numericAccountId = accountId ? Number(accountId) : undefined;
  const sessionUserId = Number(session?.claims?.sub);

  const handleClose = () => {
    setMode('list');
    setSelectedGroupId(null);
    setGroupToDelete(null);
    onClose();
  };

  const { data: account } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });

  const { data: alarmGroups = [], isLoading: isAlarmGroupsLoading } =
    useGetAlarmGroupsByAccountQuery(numericAccountId, {
      skip: !open || !numericAccountId,
    });

  const typedAlarmGroups = alarmGroups as AlarmGroupShape[];
  const isUserSubscribed =
    !Number.isFinite(sessionUserId) || typedAlarmGroups.length === 0
      ? true
      : typedAlarmGroups.some((group) =>
          group.AlarmSubscriptions?.some(
            (subscription) => subscription.User?.UserId === sessionUserId,
          ),
        );

  const { data: alarmTypes = [], isLoading: isLoadingAlarmTypes } =
    useAlarmTypesGetCollectionAsyncByqueryOptionsQuery(
      {
        orderby: 'AlarmTypeId',
        top: 10000,
      },
      {
        skip: !open || mode !== 'edit',
      },
    );

  const { data: selectedAlarmGroup, isLoading: isLoadingSelectedGroup } =
    useAlarmGroupsGetAsyncByidQuery(
      { id: selectedGroupId ?? 0 },
      {
        skip: !open || mode !== 'edit' || !selectedGroupId,
      },
    );

  const [deleteAlarmGroup] = useAlarmGroupsDeleteAsyncByidMutation();

  const title = useMemo(() => {
    if (mode === 'create') {
      return t('dialog.create_new_alarm_group', { ns: 'alarms' });
    }

    if (mode === 'edit') {
      return t('alarmGroups.editGroup', { ns: 'alarms' });
    }

    return t('alarm_setup.advanced_action', { ns: 'energy-analysis' });
  }, [mode, t]);

  const handleEditAlarmGroup = (group: ReadAlarmGroupDto) => {
    setSelectedGroupId(group.AlarmGroupId ?? null);
    setMode('edit');
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    const deleteResult = await deleteAlarmGroup({
      id: groupToDelete.AlarmGroupId,
    });

    if (!('error' in deleteResult)) {
      dispatch(
        showSnackbar({
          message:
            t('messages.alarm_group_deleted', { ns: 'alarms' }) ||
            'Alarm group deleted successfully',
          severity: 'success',
        }),
      );
      setGroupToDelete(null);
      setSelectedGroupId(null);
      setMode('list');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pr: 7 }}>
        {title}
        <IconButton
          aria-label={t('form.cancel', { ns: 'alarms' })}
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseRoundedIcon />
        </IconButton>
        {mode === 'edit' && selectedAlarmGroup ? (
          <IconButton
            aria-label={t('dialog.delete_alarm_group', { ns: 'alarms' })}
            onClick={() => setGroupToDelete(selectedAlarmGroup)}
            sx={{ position: 'absolute', right: 56, top: 8 }}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <GatewayLoadingIndicator
          isLoading={
            isAlarmGroupsLoading ||
            isLoadingSelectedGroup ||
            isLoadingAlarmTypes
          }
        />

        {mode === 'list' ? (
          <Stack spacing={2}>
            {!isUserSubscribed ? (
              <Alert severity="info">
                {t('alarmGroups.notSubscribed', { ns: 'alarms' })}
              </Alert>
            ) : null}

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setMode('create')}
              >
                {t('toolbar.new_alarm_group', {
                  ns: 'alarms',
                  defaultValue: 'New',
                })}
              </Button>
            </Box>

            <AlarmGroupDataGrid
              alarmGroups={alarmGroups}
              userOptions={account?.UserRoles}
              onEditAlarmGroup={handleEditAlarmGroup}
            />
          </Stack>
        ) : null}

        {mode === 'create' ? (
          <CreateAlarmGroupForm
            selectedAccountId={account?.AccountId}
            userOptions={account?.UserRoles || []}
            onSuccess={() => setMode('list')}
            onCancel={() => setMode('list')}
          />
        ) : null}

        {mode === 'edit' && selectedAlarmGroup ? (
          <EditAlarmGroupForm
            key={selectedAlarmGroup.AlarmGroupId}
            alarmGroup={selectedAlarmGroup}
            userOptions={account?.UserRoles || []}
            alarmTypeOptions={alarmTypes as ReadAlarmTypeDto[]}
            onSuccess={() => setMode('list')}
            onCancel={() => setMode('list')}
          />
        ) : null}

        <DeleteAlarmGroupDialog
          open={!!groupToDelete}
          groupToDelete={groupToDelete}
          users={account?.UserRoles || []}
          alarmTypes={alarmTypes as ReadAlarmTypeDto[]}
          onConfirm={handleConfirmDelete}
          onCancel={() => setGroupToDelete(null)}
        />
      </DialogContent>
    </Dialog>
  );
}
